import { Button, Card } from '@mui/material';
import Divider from '@mui/material/Divider';
import Stack from '@mui/material/Stack';
import type { MonthlyParkingPaymentParameters } from '@parkchirp/reporting';
import type { Dayjs } from 'dayjs';
import { useMemo, useState } from 'react';
import { AccountFilter, type AccountFilterValue } from './AccountFilter';
import { DateRangeFilter, type DateRangeValue } from './DateRangeFilter';
import { FacilityHierarchyFilter, type FacilityHierarchyFilterValue } from './FacilityHierarchyFilter';
import { paymentReportsInitialDateRange, paymentReportsInitialFacilityHierarchy } from './shared';
import { reportingPanelStyles } from './styles';
import type { ReportFormProps } from './types';

const addDate = (
	parameters: MonthlyParkingPaymentParameters,
	key: 'transactionStartDate' | 'transactionEndDate' | 'applicationStartDate' | 'applicationEndDate',
	date: Dayjs | null,
) => {
	if (date) {
		parameters[key] = date.format('YYYY-MM-DD');
	}
};

const addHierarchyIds = (
	parameters: MonthlyParkingPaymentParameters,
	key: 'areaIds' | 'branchIds' | 'districtIds',
	ids: number[],
) => {
	if (ids.length > 0) {
		parameters[key] = ids;
	}
};

const addFacilityIds = (parameters: MonthlyParkingPaymentParameters, facilityIds: string[]) => {
	if (facilityIds.length > 0) {
		parameters.facilityIds = facilityIds;
	}
};

export const MonthlyParkingPaymentForm = ({
	createReport,
	isGeneratingReport,
	organizationId,
	onValidationError,
}: ReportFormProps) => {
	const [dateRange, setDateRange] = useState<DateRangeValue>(paymentReportsInitialDateRange);
	const [accountFilter, setAccountFilter] = useState<AccountFilterValue>({ type: 'all' });
	const [facilityHierarchy, setFacilityHierarchy] = useState<FacilityHierarchyFilterValue>(
		paymentReportsInitialFacilityHierarchy,
	);

	const canSubmit = useMemo(() => {
		if (dateRange.dateBasis === 'transactionDate') {
			return Boolean(dateRange.transactionStartDate);
		}

		return Boolean(dateRange.applicationStartDate);
	}, [dateRange]);

	const reset = () => {
		setDateRange(paymentReportsInitialDateRange);
		setAccountFilter({ type: 'all' });
		setFacilityHierarchy(paymentReportsInitialFacilityHierarchy);
	};

	const buildAccountFilter = (): MonthlyParkingPaymentParameters['accountFilter'] => {
		switch (accountFilter.type) {
			case 'accountId':
				return {
					type: 'accountId',
					value: Number(accountFilter.value),
				};

			case 'subscriptionName':
				return {
					type: 'subscriptionName',
					value: accountFilter.value.trim(),
				};
			default:
				return undefined;
		}
	};

	const buildParameters = (): MonthlyParkingPaymentParameters => {
		const accountFilter = buildAccountFilter();
		let parameters: MonthlyParkingPaymentParameters;

		if (dateRange.dateBasis === 'transactionDate') {
			if (!dateRange.transactionStartDate) {
				throw new Error('Select a transaction start date.');
			}

			parameters = {
				dateOrder: 'transactionDate',
				transactionStartDate: dateRange.transactionStartDate.format('YYYY-MM-DD'),
			};

			addDate(parameters, 'transactionEndDate', dateRange.transactionEndDate);
			addDate(parameters, 'applicationStartDate', dateRange.applicationStartDate);
			addDate(parameters, 'applicationEndDate', dateRange.applicationEndDate);
		} else {
			if (!dateRange.applicationStartDate) {
				throw new Error('Select an application start date.');
			}

			parameters = {
				dateOrder: 'applicationDate',
				applicationStartDate: dateRange.applicationStartDate.format('YYYY-MM-DD'),
			};

			addDate(parameters, 'applicationEndDate', dateRange.applicationEndDate);
			addDate(parameters, 'transactionStartDate', dateRange.transactionStartDate);
			addDate(parameters, 'transactionEndDate', dateRange.transactionEndDate);
		}

		if (accountFilter) {
			parameters.accountFilter = accountFilter;
		}

		addHierarchyIds(
			parameters,
			'areaIds',
			facilityHierarchy.areas.map((area) => area.id),
		);

		addHierarchyIds(
			parameters,
			'branchIds',
			facilityHierarchy.branches.map((branch) => branch.id),
		);

		addHierarchyIds(
			parameters,
			'districtIds',
			facilityHierarchy.districts.map((district) => district.id),
		);

		addFacilityIds(
			parameters,
			facilityHierarchy.facilities.map((facility) => facility.id),
		);

		return parameters;
	};

	const validate = () => {
		if (dateRange.dateBasis === 'transactionDate' && !dateRange.transactionStartDate) {
			onValidationError('Select a transaction start date.');
			return false;
		}

		if (dateRange.dateBasis === 'applicationDate' && !dateRange.applicationStartDate) {
			onValidationError('Select an application start date.');
			return false;
		}

		if (accountFilter.type === 'accountId' && !accountFilter.value) {
			onValidationError('Enter an account ID.');
			return false;
		}

		if (accountFilter.type === 'subscriptionName' && !accountFilter.value.trim()) {
			onValidationError('Enter a subscription name.');
			return false;
		}

		return true;
	};

	const submit = async () => {
		if (!validate()) {
			return;
		}

		await createReport({
			reportType: 'monthlyParkingPayment',
			parameters: buildParameters(),
		});
	};

	return (
		<Card
			variant='outlined'
			sx={(theme) => ({
				...reportingPanelStyles(theme),
				overflow: 'hidden',
				p: 3,
			})}
		>
			<Stack spacing={2.5}>
				<DateRangeFilter value={dateRange} onChange={setDateRange} />

				<Divider />

				<AccountFilter filter={accountFilter} onChange={setAccountFilter} />

				<Divider />

				<FacilityHierarchyFilter
					organizationId={organizationId}
					value={facilityHierarchy}
					onChange={setFacilityHierarchy}
				/>

				<Divider />

				<Stack
					direction='row'
					justifyContent='flex-end'
					spacing={2}
					sx={{
						borderColor: 'divider',
						borderTop: 1,
						pt: 2,
					}}
				>
					<Button onClick={reset} disabled={isGeneratingReport}>
						Reset
					</Button>

					<Button onClick={submit} disabled={!canSubmit || isGeneratingReport}>
						Generate Report
					</Button>
				</Stack>
			</Stack>
		</Card>
	);
};
